import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "../lib/api";
import { CATEGORIES } from "../lib/constants";
import { useAuth } from "../lib/auth";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { toast } from "sonner";
import { ArrowRight } from "@phosphor-icons/react";

export default function CreateEvent() {
  const { user, loading } = useAuth();
  const navigate = useNavigate();
  const [brandsMap, setBrandsMap] = useState({});
  const [form, setForm] = useState({
    title: "",
    description: "",
    category: "supercar",
    brands: [],
    brand_input: "",
    date_start: "",
    date_end: "",
    location_name: "",
    city: "",
    country: "Italia",
    lat: "",
    lng: "",
    cover_image: "",
    website: "",
  });
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (!loading && !user) {
      toast.error("Devi accedere per aggiungere un evento");
      navigate("/login");
    }
  }, [user, loading, navigate]);

  useEffect(() => {
    api.get("/categories").then(({ data }) => setBrandsMap(data.brands_by_category)).catch(() => {});
  }, []);

  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const toggleBrand = (b) => {
    setForm(f => ({ ...f, brands: f.brands.includes(b) ? f.brands.filter(x => x !== b) : [...f.brands, b] }));
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!form.title || !form.description || !form.date_start || !form.city || !form.country) {
      toast.error("Compila tutti i campi obbligatori");
      return;
    }
    setSubmitting(true);
    try {
      const payload = {
        title: form.title,
        description: form.description,
        category: form.category,
        brands: form.brands,
        date_start: form.date_start,
        date_end: form.date_end || null,
        location_name: form.location_name || form.city,
        city: form.city,
        country: form.country,
        lat: parseFloat(form.lat) || 0,
        lng: parseFloat(form.lng) || 0,
        cover_image: form.cover_image || null,
        website: form.website || null,
      };
      const { data } = await api.post("/events", payload);
      toast.success("Evento inviato! In attesa di approvazione.");
      // For MVP, auto-approve so users see their event immediately
      await api.post(`/events/${data.event_id}/approve`);
      navigate(`/events/${data.event_id}`);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Errore invio");
    } finally {
      setSubmitting(false);
    }
  };

  const brandsForCat = brandsMap[form.category] || [];

  return (
    <div className="max-w-4xl mx-auto px-6 lg:px-12 py-12">
      <div className="mb-10">
        <div className="text-xs font-bold uppercase tracking-[0.3em] text-primary mb-3">Community</div>
        <h1 className="font-display text-4xl sm:text-5xl font-black uppercase tracking-tighter" data-testid="submit-title">Aggiungi un evento</h1>
        <p className="text-neutral-400 mt-3">Condividi il tuo raduno con la community. Approvazione automatica per il MVP.</p>
      </div>

      <form onSubmit={submit} className="space-y-6 border border-border rounded-sm bg-card p-6 lg:p-10">
        <div>
          <label className="text-xs font-bold uppercase tracking-widest text-neutral-400 mb-2 block">Titolo *</label>
          <Input data-testid="form-title" value={form.title} onChange={e => set("title", e.target.value)} required className="bg-background border-border rounded-sm h-12" />
        </div>
        <div>
          <label className="text-xs font-bold uppercase tracking-widest text-neutral-400 mb-2 block">Descrizione *</label>
          <Textarea data-testid="form-description" value={form.description} onChange={e => set("description", e.target.value)} required rows={5} className="bg-background border-border rounded-sm" />
        </div>
        <div className="grid md:grid-cols-2 gap-6">
          <div>
            <label className="text-xs font-bold uppercase tracking-widest text-neutral-400 mb-2 block">Categoria *</label>
            <Select value={form.category} onValueChange={(v) => { set("category", v); set("brands", []); }}>
              <SelectTrigger data-testid="form-category" className="bg-background border-border rounded-sm h-12">
                <SelectValue />
              </SelectTrigger>
              <SelectContent className="bg-card border-border">
                {CATEGORIES.map(c => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div>
            <label className="text-xs font-bold uppercase tracking-widest text-neutral-400 mb-2 block">Sito web (opzionale)</label>
            <Input data-testid="form-website" value={form.website} onChange={e => set("website", e.target.value)} placeholder="https://..." className="bg-background border-border rounded-sm h-12" />
          </div>
        </div>

        <div>
          <label className="text-xs font-bold uppercase tracking-widest text-neutral-400 mb-2 block">Marche</label>
          <div className="flex flex-wrap gap-2">
            {brandsForCat.map(b => (
              <button
                type="button"
                key={b}
                onClick={() => toggleBrand(b)}
                data-testid={`form-brand-${b}`}
                className={`px-3 py-1.5 text-xs font-bold uppercase tracking-wider border rounded-sm transition-colors ${form.brands.includes(b) ? "bg-primary text-white border-primary" : "border-border text-neutral-300 hover:border-primary/50"}`}
              >{b}</button>
            ))}
          </div>
        </div>

        <div className="grid md:grid-cols-2 gap-6">
          <div>
            <label className="text-xs font-bold uppercase tracking-widest text-neutral-400 mb-2 block">Data inizio *</label>
            <Input data-testid="form-date-start" type="date" value={form.date_start} onChange={e => set("date_start", e.target.value)} required className="bg-background border-border rounded-sm h-12" />
          </div>
          <div>
            <label className="text-xs font-bold uppercase tracking-widest text-neutral-400 mb-2 block">Data fine</label>
            <Input data-testid="form-date-end" type="date" value={form.date_end} onChange={e => set("date_end", e.target.value)} className="bg-background border-border rounded-sm h-12" />
          </div>
        </div>

        <div className="grid md:grid-cols-3 gap-6">
          <div>
            <label className="text-xs font-bold uppercase tracking-widest text-neutral-400 mb-2 block">Luogo</label>
            <Input data-testid="form-location" value={form.location_name} onChange={e => set("location_name", e.target.value)} placeholder="Nome del luogo" className="bg-background border-border rounded-sm h-12" />
          </div>
          <div>
            <label className="text-xs font-bold uppercase tracking-widest text-neutral-400 mb-2 block">Città *</label>
            <Input data-testid="form-city" value={form.city} onChange={e => set("city", e.target.value)} required className="bg-background border-border rounded-sm h-12" />
          </div>
          <div>
            <label className="text-xs font-bold uppercase tracking-widest text-neutral-400 mb-2 block">Paese *</label>
            <Input data-testid="form-country" value={form.country} onChange={e => set("country", e.target.value)} required className="bg-background border-border rounded-sm h-12" />
          </div>
        </div>

        <div className="grid md:grid-cols-2 gap-6">
          <div>
            <label className="text-xs font-bold uppercase tracking-widest text-neutral-400 mb-2 block">Latitudine</label>
            <Input data-testid="form-lat" type="number" step="any" value={form.lat} onChange={e => set("lat", e.target.value)} placeholder="45.4642" className="bg-background border-border rounded-sm h-12" />
          </div>
          <div>
            <label className="text-xs font-bold uppercase tracking-widest text-neutral-400 mb-2 block">Longitudine</label>
            <Input data-testid="form-lng" type="number" step="any" value={form.lng} onChange={e => set("lng", e.target.value)} placeholder="9.1900" className="bg-background border-border rounded-sm h-12" />
          </div>
        </div>

        <div>
          <label className="text-xs font-bold uppercase tracking-widest text-neutral-400 mb-2 block">URL immagine copertina</label>
          <Input data-testid="form-cover" value={form.cover_image} onChange={e => set("cover_image", e.target.value)} placeholder="https://..." className="bg-background border-border rounded-sm h-12" />
        </div>

        <Button type="submit" disabled={submitting} data-testid="form-submit" className="w-full h-14 bg-primary hover:bg-primary/90 text-white font-black uppercase tracking-wider rounded-sm">
          {submitting ? "Invio..." : "Pubblica evento"} <ArrowRight size={16} weight="bold" className="ml-2" />
        </Button>
      </form>
    </div>
  );
}
